import { JSX, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { Label } from 'components/Label';
import { ReferenceCandidates } from 'components/ReferenceCandidates/ReferenceCandidates';
import { useReferenceCandidates } from 'components/ReferenceCandidates/useReferenceCandidates';
import { getUnusedReferenceCandidates } from 'components/ReferenceCandidates/utils';
import { Select } from 'components/Select';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { useFetchServiceTypes } from 'hooks/useFetchServiceTypes';

import {
  PendingReference,
  PlsReferenceFieldScope,
  ReferenceFields,
  withNewPendingReference,
} from './ReferenceFields';
import { PLS_SECTION_LABELS } from './constants';
import { usePlsReferenceCandidateAdapter } from './hooks/usePlsReferenceCandidateAdapter';
import {
  PlsFormValues,
  PlsOperationalFieldDefinition,
  PlsReferenceFieldDefinition,
} from './types';
import { toSelectStringValue } from './utils';

type OptionalFieldsSectionProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  tmsID?: number;
  loadReferenceFields: PlsReferenceFieldDefinition[];
  isLoadingLoadReferenceFields: boolean;
  loadReferenceFieldsError?: unknown;
  operationalFieldDefinitions: PlsOperationalFieldDefinition[];
};

export function OptionalFieldsSection({
  formMethods,
  tmsID,
  loadReferenceFields,
  isLoadingLoadReferenceFields,
  loadReferenceFieldsError,
  operationalFieldDefinitions,
}: OptionalFieldsSectionProps): JSX.Element {
  const { control, getValues, setValue, clearErrors } = formMethods;
  const referenceCandidates = useReferenceCandidates();
  const pendingReferences =
    useWatch({ control, name: 'plsPendingReferences' }) ?? [];
  // Build Load's error on a card isn't a field rule, so nothing clears it but a change to the cards.
  const setPendingReferences = (pending: PendingReference[]) => {
    clearErrors('plsPendingReferences');
    setValue('plsPendingReferences', pending, { shouldDirty: true });
  };
  const referenceCandidateAdapter = usePlsReferenceCandidateAdapter({
    loadReferenceFields,
    operationalFields: operationalFieldDefinitions,
    startReference: (number) =>
      setPendingReferences(
        withNewPendingReference(getValues('plsPendingReferences') ?? [], number)
      ),
  });
  // A number in a card that has no type yet is in use too.
  const usedNumbers = [
    ...referenceCandidateAdapter.usedNumbers,
    ...pendingReferences.map((pending) => pending.value),
  ];
  const unusedCandidateNumbers = getUnusedReferenceCandidates(
    referenceCandidates,
    usedNumbers
  ).map((candidate) => candidate.number);
  const customerReferenceTypes = useMemo(
    () =>
      operationalFieldDefinitions
        .filter((field) => field.code?.trim())
        .map((field) => ({
          value: field.code,
          label: `${field.name} (customer reference)`,
        })),
    [operationalFieldDefinitions]
  );
  const { serviceTypes, isLoading: isLoadingServiceTypes } =
    useFetchServiceTypes(tmsID ?? 0);

  const serviceTypeOptions = (
    Array.isArray(serviceTypes) ? serviceTypes : []
  ).reduce<{ value: string; label: string }[]>((options, serviceType) => {
    const value = String(serviceType.id ?? '').trim();
    if (!value || options.some((option) => option.value === value)) {
      return options;
    }

    options.push({
      value,
      label: String(serviceType.name ?? value).trim() || value,
    });
    return options;
  }, []);

  return (
    <Flex direction='col' gap='md'>
      <div>
        <Flex align='center' gap='sm'>
          <Label name='specifications.serviceType'>
            {PLS_SECTION_LABELS.serviceType}
          </Label>
          {isLoadingServiceTypes && <ButtonLoader className='h-3 w-3' />}
        </Flex>
        <Controller
          name='plsLoadServiceTypeId'
          control={control}
          render={({ field }) => (
            <div
              onPointerDownCapture={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest('button[aria-label="Clear selection"]')) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
              <Select
                showSearch
                allowClear
                name={field.name}
                className='h-8'
                placeholder={
                  isLoadingServiceTypes
                    ? 'Loading service types...'
                    : 'Select service type (optional)'
                }
                disabled={isLoadingServiceTypes}
                optionFilterProp='label'
                onChange={(value, option) => {
                  const serviceTypeID = toSelectStringValue(value);
                  const selectedOption = Array.isArray(option)
                    ? option[0]
                    : option;
                  field.onChange(serviceTypeID);
                  setValue(
                    'specifications.serviceType',
                    serviceTypeID ? String(selectedOption?.label ?? '') : '',
                    { shouldDirty: true }
                  );
                }}
                value={field.value ?? ''}
                options={serviceTypeOptions}
              />
            </div>
          )}
        />
      </div>

      <div className='w-full'>
        <div className='mb-2'>
          <Label name='plsLoadReferenceFields' hideAIHint>
            Load Reference Fields
          </Label>
        </div>
        <ReferenceFields
          definitions={loadReferenceFields}
          scope={PlsReferenceFieldScope.Load}
          isLoading={isLoadingLoadReferenceFields}
          error={loadReferenceFieldsError}
          candidateNumbers={unusedCandidateNumbers}
          pendingReferences={pendingReferences}
          onPendingReferencesChange={setPendingReferences}
          otherReferenceTypes={customerReferenceTypes}
          onAddOtherReference={(code, value) =>
            referenceCandidateAdapter.addReference(value, code)
          }
        />
        <div className='mt-3 w-full empty:hidden'>
          <ReferenceCandidates
            candidates={referenceCandidates}
            adapter={{ ...referenceCandidateAdapter, usedNumbers }}
          />
        </div>
      </div>
    </Flex>
  );
}
